import { useCallback, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  parseISO,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useAppointmentsQuery } from '@/hooks/useAppointmentsQuery';
import { useExternalCalendarEventsQuery } from '@/hooks/useCalendarSync';
import { formatDateToIsoDateOnly, formatDateToMonthAndYear } from '@/utils/dateFormatting';
import { MonthView } from './MonthView';
import { WeekView } from './WeekView';
import { DayView } from './DayView';
import { DayDetailsPanel } from './DayDetailsPanel';
import { AppointmentModal } from './AppointmentModal';
import { AppointmentPreviewModal } from './AppointmentPreviewModal';
import { TherapyTypeLegend } from './TherapyTypeLegend';
import { filterAppointmentsForIsoDate } from './utils/appointmentFiltering';
import { CALENDAR_VIEW_MODES, type CalendarViewMode } from './types';
import type { Appointment } from '@/api/appointments/appointmentsApi.types';
import { useAppointmentDeepLink } from './useAppointmentDeepLink';

export function CalendarView() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState<CalendarViewMode>('month');
  const [currentDate, setCurrentDate] = useState(function initializeCurrentDate() {
    return new Date();
  });
  const [selectedDate, setSelectedDate] = useState(function initializeSelectedDate() {
    return new Date();
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState(false);
  const [appointmentToEdit, setAppointmentToEdit] = useState<Appointment | null>(null);
  const [appointmentToPreview, setAppointmentToPreview] = useState<Appointment | null>(null);

  const appointmentsQuery = useAppointmentsQuery();
  const appointments = appointmentsQuery.data === undefined ? [] : appointmentsQuery.data;

  const focusAppointment = useCallback(function focusAppointment(appointment: Appointment) {
    const appointmentDay = parseISO(appointment.date);
    setCurrentDate(appointmentDay);
    setSelectedDate(appointmentDay);
    setAppointmentToPreview(appointment);
  }, []);
  useAppointmentDeepLink(appointments, appointmentsQuery.data !== undefined, focusAppointment);
  const appointmentsForSelectedDate = filterAppointmentsForIsoDate(
    appointments,
    formatDateToIsoDateOnly(selectedDate)
  );

  const visibleRangeStartIsoDate = formatDateToIsoDateOnly(
    startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 })
  );
  const visibleRangeEndIsoDate = formatDateToIsoDateOnly(
    endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 })
  );
  const externalEventsQuery = useExternalCalendarEventsQuery(
    visibleRangeStartIsoDate,
    visibleRangeEndIsoDate
  );
  const externalEvents = externalEventsQuery.data === undefined ? [] : externalEventsQuery.data;

  function goToPreviousPeriod() {
    if (viewMode === 'month') {
      setCurrentDate(subMonths(currentDate, 1));
      return;
    }
    if (viewMode === 'week') {
      setCurrentDate(subWeeks(currentDate, 1));
      return;
    }
    setCurrentDate(subDays(currentDate, 1));
  }

  function goToNextPeriod() {
    if (viewMode === 'month') {
      setCurrentDate(addMonths(currentDate, 1));
      return;
    }
    if (viewMode === 'week') {
      setCurrentDate(addWeeks(currentDate, 1));
      return;
    }
    setCurrentDate(addDays(currentDate, 1));
  }

  function goToToday() {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  }

  function selectDay(day: Date) {
    setSelectedDate(day);
    setIsDetailsPanelOpen(true);
  }

  function openAddAppointmentModal(dateForNewAppointment: Date) {
    setSelectedDate(dateForNewAppointment);
    setAppointmentToEdit(null);
    setIsModalOpen(true);
  }

  function openEditAppointmentModal(appointment: Appointment) {
    setAppointmentToEdit(appointment);
    setIsModalOpen(true);
  }

  function closeAppointmentModal() {
    setIsModalOpen(false);
    setAppointmentToEdit(null);
  }

  function openPreviewAppointmentModal(appointment: Appointment) {
    setAppointmentToPreview(appointment);
  }

  function closePreviewAppointmentModal() {
    setAppointmentToPreview(null);
  }

  function editAppointmentFromPreview(appointment: Appointment) {
    setAppointmentToPreview(null);
    openEditAppointmentModal(appointment);
  }

  function openSessionNoteFromPreview(appointment: Appointment) {
    setAppointmentToPreview(null);
    navigate(
      generatePath(APPLICATION_ROUTES.workspacePatientSessionNote, {
        patientId: appointment.patientId,
        noteId: 'new',
      }) +
        '?appointmentId=' +
        appointment.id
    );
  }

  return (
    <Card
      padding="none"
      className="overflow-hidden flex flex-col h-full [container-type:inline-size]"
    >
      <div className="p-6 border-b border-zooborder flex flex-wrap justify-between items-center gap-x-4 gap-y-3">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 whitespace-nowrap text-zooindigo font-bold text-lg capitalize">
            <Icon name="Calendar" className="text-zooviolet" />
            {formatDateToMonthAndYear(currentDate)}
          </div>
          <div className="flex items-center gap-1 bg-zoovpalelavender rounded-lg p-1 border border-zooborder">
            <button
              type="button"
              onClick={goToPreviousPeriod}
              aria-label={t('calendar:previousPeriod')}
              className="p-1.5 hover:bg-zoowhite rounded-md transition-colors text-zooindigo"
            >
              <Icon name="ChevronLeft" />
            </button>
            <button
              type="button"
              onClick={goToToday}
              className="px-3 py-1 hover:bg-zoowhite rounded-md transition-colors text-xs font-bold text-zooindigo uppercase tracking-wider"
            >
              {t('calendar:today')}
            </button>
            <button
              type="button"
              onClick={goToNextPeriod}
              aria-label={t('calendar:nextPeriod')}
              className="p-1.5 hover:bg-zoowhite rounded-md transition-colors text-zooindigo"
            >
              <Icon name="ChevronRight" />
            </button>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 [@container(max-width:50rem)]:w-full [@container(max-width:50rem)]:justify-between">
          <div className="flex bg-zoovpalelavender rounded-lg p-1 border border-zooborder">
            {CALENDAR_VIEW_MODES.map(function renderViewModeButton(calendarViewMode) {
              const isActiveViewMode = viewMode === calendarViewMode;
              return (
                <button
                  key={calendarViewMode}
                  type="button"
                  onClick={function activateViewMode() {
                    setViewMode(calendarViewMode);
                  }}
                  className={cn(
                    'px-4 py-1.5 rounded-md text-sm font-medium transition-all',
                    isActiveViewMode
                      ? 'bg-zoowhite text-zooprimary shadow-sm'
                      : 'text-zoomuted hover:text-zooindigo'
                  )}
                >
                  {t('calendar:views.' + calendarViewMode)}
                </button>
              );
            })}
          </div>
          <Button
            onClick={function addAppointmentForSelectedDate() {
              openAddAppointmentModal(selectedDate);
            }}
            aria-label={t('calendar:addAppointment')}
            className="flex items-center gap-2"
          >
            <Icon name="Plus" />
            <span className="hidden sm:inline">{t('calendar:addAppointment')}</span>
          </Button>
        </div>
      </div>

      <TherapyTypeLegend />

      <div className="flex-1 flex flex-col overflow-hidden relative min-h-[500px]">
        {appointmentsQuery.isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-zoowhite bg-opacity-80 z-20">
            <div className="flex flex-col items-center gap-3">
              <LoadingSpinner className="w-10 h-10 border-4" />
              <p className="text-sm font-medium text-zoomuted">{t('calendar:loading')}</p>
            </div>
          </div>
        )}

        {appointmentsQuery.isError && (
          <div className="m-4 mb-0 p-3 bg-zooerrorbg border border-zooerrorborder rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shrink-0 z-10 relative">
            <div className="flex items-center gap-3">
              <div className="text-zooerrortext text-xl flex-shrink-0">
                <Icon name="AlertCircle" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-zooerrortext">
                  {t('calendar:authError.title')}
                </h4>
                <p className="text-xs text-zooerrortext mt-0.5 opacity-90">
                  {t('calendar:authError.description')}
                </p>
              </div>
            </div>
            <Button
              variant="destructive"
              size="small"
              onClick={function retryLoadingAppointments() {
                appointmentsQuery.refetch();
              }}
              className="whitespace-nowrap w-full sm:w-auto"
            >
              {t('common:actions.retry')}
            </Button>
          </div>
        )}

        <div className="flex-1 overflow-hidden relative mt-0">
          {viewMode === 'month' && (
            <MonthView
              currentDate={currentDate}
              selectedDate={selectedDate}
              appointments={appointments}
              onDaySelected={selectDay}
            />
          )}
          {viewMode === 'week' && (
            <WeekView
              currentDate={currentDate}
              selectedDate={selectedDate}
              appointments={appointments}
              externalEvents={externalEvents}
              onAppointmentSelected={openEditAppointmentModal}
            />
          )}
          {viewMode === 'day' && (
            <DayView
              currentDate={currentDate}
              appointments={appointments}
              externalEvents={externalEvents}
              onAppointmentSelected={openEditAppointmentModal}
            />
          )}
        </div>
      </div>

      {isModalOpen && (
        <AppointmentModal
          appointmentToEdit={appointmentToEdit}
          initialDate={selectedDate}
          appointments={appointments}
          externalEvents={externalEvents}
          onClose={closeAppointmentModal}
        />
      )}

      {isDetailsPanelOpen && (
        <DayDetailsPanel
          selectedDate={selectedDate}
          appointmentsForSelectedDate={appointmentsForSelectedDate}
          onClose={function closeDetailsPanel() {
            setIsDetailsPanelOpen(false);
          }}
          onEditAppointment={openEditAppointmentModal}
          onPreviewAppointment={openPreviewAppointmentModal}
          onOpenSessionNote={openSessionNoteFromPreview}
          onAddAppointment={openAddAppointmentModal}
        />
      )}

      {appointmentToPreview !== null && (
        <AppointmentPreviewModal
          appointment={appointmentToPreview}
          onClose={closePreviewAppointmentModal}
          onEdit={editAppointmentFromPreview}
          onOpenSessionNote={openSessionNoteFromPreview}
        />
      )}
    </Card>
  );
}
